import { useState, useMemo, useEffect } from 'react';
import api, { RelatoriosApi, ColaboradoresApi, ConfigApi } from '../api/client.js';
import { useQuery } from '@tanstack/react-query';
import { PieChart, Pie, Cell, Tooltip as RTooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { Table } from '../components/Table.jsx';
import { formatDate } from '../utils/formatData.js';
import { useAuth } from '../context/AuthContext.jsx';

export default function RelatoriosPage() {
  const { perfil, auth } = useAuth();
  const perfilNormalizado = String(perfil || '').trim().toUpperCase();
  const isAuthReady = Boolean(perfilNormalizado);
  const isColaborador = perfilNormalizado === 'COLABORADOR';
  const colaboradorFromAuth =
    auth?.usuario?.colaboradorId || auth?.usuario?.colaborador_id || '';
  const colaboradorIdStorage = localStorage.getItem('defaultColaboradorId') || '';
  const { data: config } = useQuery({
    queryKey: ['config'],
    queryFn: () => ConfigApi.obter(),
  });
  const { data: colResp } = useQuery({
    enabled: isAuthReady && !isColaborador,
    queryKey:['colaboradores'],
    queryFn: () => ColaboradoresApi.listar()
  });
  const { data: colaboradorMeResp } = useQuery({
    enabled: isAuthReady && isColaborador,
    queryKey: ['colaborador-me'],
    queryFn: () => ColaboradoresApi.me(),
  });
  // A API de colaboradores retorna { data:[], meta:{...} }; normalizamos aqui para evitar erros de .map
  const colaboradores = Array.isArray(colResp) ? colResp : (colResp?.data || []);
  const [colaboradorId,setColaboradorId] = useState(
    isColaborador ? (colaboradorIdStorage || colaboradorFromAuth) : '',
  );
  const [ano,setAno] = useState(new Date().getFullYear());
  const [mes,setMes] = useState(new Date().getMonth()+1);
  const { data: relatorio, refetch, isFetching } = useQuery({ enabled:false, queryKey:['relatorio', colaboradorId, ano, mes], queryFn: () => RelatoriosApi.mensal(colaboradorId, ano, mes) });

  useEffect(() => {
    if (!isColaborador) return;
    const nextId = colaboradorIdStorage || colaboradorFromAuth;
    if (!nextId || colaboradorId === nextId) return;
    setColaboradorId(nextId);
  }, [isColaborador, colaboradorIdStorage, colaboradorFromAuth, colaboradorId]);

  const gerar = e => { e.preventDefault(); refetch(); };

  const downloadFile = async (url, filename) => {
    try {
      const response = await api.get(url, { responseType: 'blob' });
      
      const blob = response.data;
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Erro ao baixar arquivo:', err);
      alert(`Erro ao baixar arquivo: ${err.message}`);
    }
  };

  const excelUrl = colaboradorId ? RelatoriosApi.mensalExcelUrl(colaboradorId, ano, mes) : '#';
  const pdfUrl = colaboradorId ? RelatoriosApi.mensalPdfUrl(colaboradorId, ano, mes) : '#';

  // Preparação dos dados para gráficos e tabela
  const registros = relatorio?.registros || [];
  const pieData = useMemo(()=>{
    if (!registros.length) return [];
    const acc = { m1:0,m2:0,m3:0,m4:0 };
    registros.forEach(r => { acc.m1+=r.m1; acc.m2+=r.m2; acc.m3+=r.m3; acc.m4+=r.m4; });
    return Object.entries(acc).map(([k,v])=>({ name:k.toUpperCase(), value:v }));
  },[registros]);

  const barData = useMemo(()=> registros.map(r => ({
    dia: r.data.slice(-2),
    m1: r.m1,
    m2: r.m2,
    m3: r.m3,
    m4: r.m4,
    total: r.total_pontos,
    valor: Number(r.valor_diario)
  })),[registros]);

  const colaboradorMe = colaboradorMeResp?.data || colaboradorMeResp || null;
  const colaboradorSelecionado = isColaborador
    ? colaboradorMe
    : colaboradores.find((c) => c.id === colaboradorId);
  const bonusMensalGlobal = Number(config?.bonus_mensal_global || 500);
  const bonusMensalColaborador = Number(colaboradorSelecionado?.bonus_mensal_padrao);
  const bonusMensalBase =
    Number.isFinite(bonusMensalColaborador) && bonusMensalColaborador > 0
      ? bonusMensalColaborador
      : bonusMensalGlobal;

  const resumoMensal = useMemo(() => {
    if (!relatorio) return null;
    const dias = Number(relatorio.diasRegistrados || 0);
    const totalPontos = Number(relatorio.totalPontos || 0);
    const totalValorDiario = Number(
      relatorio.totalValorDiario ?? registros.reduce((acc, r) => acc + Number(r.valor_diario || 0), 0),
    );
    const totalValorPenalidade = Number(
      relatorio.totalValorPenalidade ?? registros.reduce((acc, r) => acc + Number(r.valor_penalidade || 0), 0),
    );
    const totalValor = Number(relatorio.totalValor || 0);
    return {
      dias,
      totalPontos,
      totalValorDiario,
      totalValorPenalidade,
      totalValor,
    };
  }, [relatorio, registros]);

  // Paleta adaptada da imagem de referência (teal, laranja, mostarda, coral)
  // Mantemos consistência entre pizza e barras.
  const cores = [
    '#0B787E', // teal principal
    '#E9782D', // laranja vivo
    '#F3C240', // amarelo mostarda
    '#D5563A'  // coral / terracota
  ];
  const coresBordas = [
    '#0a666b',
    '#d6681f',
    '#DBAD33',
    '#BF482F'
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const total = payload.reduce((a,p)=> a + (p.value||0), 0);
    return (
      <div style={{background:'#fff', border:'1px solid var(--color-border)', padding:'.55rem .7rem', fontSize:'.6rem', borderRadius:'6px', boxShadow:'var(--shadow-md)'}}> 
        <strong style={{display:'block', marginBottom:'.25rem'}}>Dia {label}</strong>
        {payload.map(p => (
          <div key={p.dataKey} style={{display:'flex', alignItems:'center', gap:4}}>
            <span style={{display:'inline-block', width:10, height:10, background:p.fill, borderRadius:2}} />
            <span style={{textTransform:'uppercase'}}>{p.dataKey}</span>
            <span style={{marginLeft:'auto', fontWeight:600}}>{p.value}</span>
          </div>
        ))}
        <div style={{marginTop:'.35rem', borderTop:'1px solid #eee', paddingTop:'.3rem', fontWeight:600}}>Total: {total}</div>
      </div>
    );
  };

  return (
    <div>
      <h2 className="page-title">Relatórios Mensais</h2>
      <form onSubmit={gerar} className="card flex gap-sm" style={{alignItems:'flex-end', flexWrap:'wrap', marginBottom:'1rem'}}>
        <div style={{flex:'2 1 200px'}}>
          <label>Colaborador</label>
          <select
            value={colaboradorId}
            onChange={e=>setColaboradorId(e.target.value)}
            required
            disabled={isColaborador}
          >
            <option value="">Selecione...</option>
            {isColaborador && colaboradorId && (
              <option value={colaboradorId}>
                {auth?.usuario?.nome || auth?.usuario?.email || colaboradorId}
              </option>
            )}
            {colaboradores.map(c=> <option key={c.id} value={c.id}>{c.usuario?.nome || c.usuario?.email || c.nome || c.id}</option>)}
          </select>
        </div>
        <div>
          <label>Ano</label>
          <input type="number" value={ano} onChange={e=>setAno(e.target.value)} style={{width:'90px'}} />
        </div>
        <div>
          <label>Mês</label>
          <input type="number" value={mes} min={1} max={12} onChange={e=>setMes(e.target.value)} style={{width:'70px'}} />
        </div>
        <button className="btn" disabled={!colaboradorId || isFetching}>Gerar</button>
        {relatorio && <div style={{marginLeft:'auto'}} className="flex gap-sm">
          <button className="btn outline" onClick={() => downloadFile(excelUrl, `relatorio_${colaboradorId}_${ano}_${mes}.xlsx`)}>Excel</button>
          <button className="btn outline" onClick={() => downloadFile(pdfUrl, `relatorio_${colaboradorId}_${ano}_${mes}.pdf`)}>PDF</button>
        </div>}
      </form>
      {isFetching && <p>Gerando...</p>}
      {relatorio && (
        <>
        <div className="flex gap-md" style={{flexWrap:'wrap', marginBottom:'1rem'}}>
          <div className="card" style={{flex:'1 1 300px', minWidth:'260px'}}>
            <h3 style={{marginTop:0, fontSize:'.85rem'}}>Distribuição de Métricas</h3>
            {pieData.length ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" label>
                    {pieData.map((p,i)=><Cell key={i} fill={cores[i]} />)}
                  </Pie>
                  <RTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : <p style={{fontSize:'.65rem'}}>Sem dados.</p>}
          </div>
          <div className="card" style={{flex:'2 1 400px', minWidth:'320px'}}>
            <h3 style={{marginTop:0, fontSize:'.85rem'}}>Pontos por Dia</h3>
            {barData.length ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={barData} margin={{ left:4, right:8, top:10 }}>
                  <defs>
                    {cores.map((c,i)=> (
                      <linearGradient id={`gBar${i}`} key={i} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={c} stopOpacity={0.9} />
                        <stop offset="100%" stopColor={c} stopOpacity={0.55} />
                      </linearGradient>
                    ))}
                    <filter id="fShadow" x="-10%" y="-10%" width="130%" height="130%">
                      <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor="#999" floodOpacity="0.35" />
                    </filter>
                  </defs>
                  <CartesianGrid strokeDasharray="2 4" stroke="#f1dcc7" />
                  <XAxis dataKey="dia" fontSize={10} tickLine={false} />
                  <YAxis allowDecimals={false} ticks={[0,1,2,3,4]} fontSize={10} tickLine={false} axisLine={{ stroke:'#ddd' }} />
                  <RTooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{fontSize:10}} />
                  <Bar dataKey="m1" stackId="a" fill="url(#gBar0)" stroke={coresBordas[0]} radius={[4,4,0,0]} filter="url(#fShadow)" />
                  <Bar dataKey="m2" stackId="a" fill="url(#gBar1)" stroke={coresBordas[1]} radius={[4,4,0,0]} filter="url(#fShadow)" />
                  <Bar dataKey="m3" stackId="a" fill="url(#gBar2)" stroke={coresBordas[2]} radius={[4,4,0,0]} filter="url(#fShadow)" />
                  <Bar dataKey="m4" stackId="a" fill="url(#gBar3)" stroke={coresBordas[3]} radius={[4,4,0,0]} filter="url(#fShadow)" />
                </BarChart>
              </ResponsiveContainer>
            ) : <p style={{fontSize:'.65rem'}}>Sem dados.</p>}
          </div>
          <div className="card" style={{flex:'1 1 280px', minWidth:'240px'}}>
            <h3 style={{marginTop:0, fontSize:'.85rem'}}>Resumo</h3>
            <Table
              keyField="k"
              columns={[
                { key:'k', header:'Indicador', thStyle:{width:'60%'}, render:(v)=> <strong style={{fontWeight:600}}>{v}</strong> },
                {
                  key:'v',
                  header:'Valor',
                  render:(v, row)=> (
                    <span
                      style={{
                        fontFamily:'monospace',
                        color: row.color || 'inherit',
                        fontWeight: row.color ? 600 : 'normal',
                      }}
                    >
                      {v}
                    </span>
                  )
                }
              ]}
              data={resumoMensal ? [
                { k:'Bônus Mensal (R$)', v: bonusMensalBase.toFixed(2) },
                { k:'Dias Registrados', v: resumoMensal.dias },
                { k:'Total Pontos', v: resumoMensal.totalPontos },
                { k:'Valor Diário (R$)', v: resumoMensal.totalValorDiario.toFixed(2) },
                {
                  k:'Descontos Penalidades (R$)',
                  v: resumoMensal.totalValorPenalidade.toFixed(2),
                  color: resumoMensal.totalValorPenalidade < 0 ? '#dc2626' : 'inherit',
                },
                {
                  k:'Valor Líquido (R$)',
                  v: resumoMensal.totalValor.toFixed(2),
                  color:
                    resumoMensal.totalValor > 0
                      ? '#16a34a'
                      : resumoMensal.totalValor < 0
                        ? '#dc2626'
                        : 'inherit',
                },
                (() => {
                  const falta = Number((bonusMensalBase - resumoMensal.totalValor).toFixed(2));
                  return {
                    k: falta <= 0 ? 'Meta atingida (+R$)' : 'Falta para meta (R$)',
                    v: Math.abs(falta).toFixed(2),
                    color: falta <= 0 ? '#16a34a' : '#f59e0b',
                  };
                })(),
                { k:'Média Pontos/Dia', v: (resumoMensal.totalPontos / Math.max(1, resumoMensal.dias)).toFixed(2) },
                { k:'Média Valor/Dia (R$)', v: (resumoMensal.totalValorDiario / Math.max(1, resumoMensal.dias)).toFixed(2) }
              ] : []}
            />
          </div>
        </div>
        <div className="card">
          <h3 style={{marginTop:0, fontSize:'.85rem'}}>Registros Detalhados</h3>
          <Table
            keyField="id"
            columns={[
              { key:'name', header:'Nome', render:v=> v || '' },
              { key:'data', header:'Data', thStyle:{whiteSpace:'nowrap'}, render: v => formatDate(v) },
              { key:'m1', header:'M1' },
              { key:'m2', header:'M2' },
              { key:'m3', header:'M3' },
              { key:'m4', header:'M4' },
              { key:'total_pontos', header:'Total' },
              { key:'valor_diario', header:'Valor (R$)', render:v => Number(v).toFixed(2) },
              { key:'observacoes', header:'Obs', render:v=> v || '' },
            ]}
            data={registros}
          />
        </div>
        </>
      )}
    </div>
  );
}
